import type { DateTime } from "luxon";
import { useCallback } from "react";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { usePredictCost } from "../../../../hooks/usePredictCost";
import { buildCostPrediction, PREDICT_COST_FAILED_EVENT } from "../../../../utils/costPrediction";
import type { Nurse } from "../../../../utils/models";
import { countDailyHours } from "../../../../utils/statsCalculations";
import { isAgencyNurse } from "../../../../utils/warningDialogs/WarningDialog";

type ExpectedNurseStats = {
	isAgencyNurse: boolean;
	overtimeHours: number;
	/** Weekly OT before this add (drives the OvertimeCell before/after tooltip); undefined with no prediction. */
	weeklyOvertimeHoursBeforeChange: number | undefined;
	/** Whether a backend prediction was returned — gates the OT row (no data ⇒ no row). */
	hasPrediction: boolean;
	overtimeCost: string;
	predictedCost: string;
	dailyHours: number;
};

type Props = {
	weekIndex: number;
	dayIndex: number;
	shiftIndex: number;
};

export const useGetNurseExpectedStatsData = ({ weekIndex, dayIndex }: Props) => {
	const { hospitalData, chosenDepartment } = useHospitalData();
	const { mutateAsync: predictCostAsync } = usePredictCost();
	const { trackError } = useAnalytics();

	const getExpectedStats = useCallback(
		async ({
			selectedNurse,
			shiftStart,
			shiftEnd,
			jobId,
		}: {
			selectedNurse: Nurse | null | undefined;
			shiftStart: DateTime;
			shiftEnd: DateTime;
			jobId?: string;
		}): Promise<ExpectedNurseStats> => {
			if (!selectedNurse) {
				return {
					isAgencyNurse: false,
					overtimeHours: 0,
					weeklyOvertimeHoursBeforeChange: undefined,
					hasPrediction: false,
					predictedCost: "-",
					overtimeCost: "-",
					dailyHours: 0,
				};
			}
			let dailyHours = 0;
			// Default to the no-prediction result so a backend failure naturally yields the "-" display.
			let cost = buildCostPrediction(undefined, { isGlobalSalary: Boolean(selectedNurse.isGlobalSalary) });

			try {
				const prediction = await predictCostAsync({
					departmentId: chosenDepartment.department_id,
					nurseId: selectedNurse.id as number,
					startTime: shiftStart.toISO() ?? "",
					endTime: shiftEnd.toISO() ?? "",
					...(jobId ? { jobId } : {}),
				});
				// Unified cost + OT: marginal weekly delta, formatted as $X / $0 / -$X / est. $X (fixes the old
				// raw-shiftCost display and the `$-X` negative format), OT forced to 0 for global-salary nurses.
				cost = buildCostPrediction(prediction, { isGlobalSalary: Boolean(selectedNurse.isGlobalSalary) });
				const shiftHours = prediction.weeklyHoursAfter - prediction.weeklyHoursBefore;
				const { weeklyShifts = [] } = hospitalData.weeks[weekIndex] || {};
				const originalDailyHours = countDailyHours({
					nurseId: selectedNurse.id,
					departmentId: chosenDepartment.department_id,
					weeklyShifts,
					dayIndex,
					breakRulesConfig: chosenDepartment.breakRulesConfig ?? [],
					nurseUnion: selectedNurse.union,
				});
				dailyHours = originalDailyHours + shiftHours;
			} catch (error) {
				// Show unknown cost ("-") rather than an incorrect estimate, but report the failure: the "-" is
				// otherwise indistinguishable from a legitimately absent prediction, so a backend outage here
				// would silently degrade the OT/cost warnings with nothing to alert on.
				trackError(PREDICT_COST_FAILED_EVENT, {
					nurse_id: selectedNurse.id,
					department_id: chosenDepartment.department_id,
					error_message: error instanceof Error ? error.message : String(error),
				});
			}

			return {
				isAgencyNurse: isAgencyNurse(selectedNurse.employmentType),
				overtimeHours: cost.overtimeHours ?? 0,
				weeklyOvertimeHoursBeforeChange: cost.weeklyOvertimeHoursBeforeChange,
				// costValue is undefined only when there was no prediction — the signal for "no OT data to show".
				hasPrediction: cost.costValue !== undefined,
				predictedCost: cost.costDisplay,
				overtimeCost: cost.costDisplay,
				dailyHours,
			};
		},
		[weekIndex, hospitalData, chosenDepartment, dayIndex, predictCostAsync, trackError],
	);

	return {
		getExpectedStats,
	};
};
